import { action, makeObservable, observable, when, runInAction, computed } from 'mobx';
import moment from 'moment';
import debounce from 'lodash.debounce';
import { TFlutterChart, TLoadHistoryParams, TQuote } from 'src/types';
import { createChartElement, runChartApp } from 'src/flutter-chart';
import Painter from 'src/flutter-chart/painter';
import { safeParse } from 'src/utils';
import { capitalize } from 'src/components/ui/utils';
import MainStore from '.';

export default class ChartAdapterStore {
    private mainStore: MainStore;
    isChartLoaded = false;
    replayFeedCount = 0;
    flutterChart?: TFlutterChart;
    epochBounds = {
        leftEpoch: 0,
        rightEpoch: 0,
    };
    quoteBounds = {
        topQuote: 0,
        bottomQuote: 0,
    };
    isFeedLoaded = false;
    msPerPx?: number;
    drawingHoverIndex: number | undefined | null = null;
    isDataFitModeEnabled = false;
    isXScrollBlocked = false;
    painter = new Painter();
    drawingColor = 0;
    isScaled = false;
    
    // Flag to suppress user-scroll detection during programmatic scrolls
    private _isProgrammaticScroll = false;
    private _programmaticScrollTimer?: ReturnType<typeof setTimeout>;
    
    crossHairValue?: {
        x: number;
        y: number;
        xLocal: number;
        yLocal: number;
        bottomIndex: number | undefined;
    } = {
            x: 0,
            y: 0,
            xLocal: 0,
            yLocal: 0,
            bottomIndex: 0,
        };
    touchValues: {
        deltaXTotal?: number;
        deltaYTotal?: number;
        multiTouch?: boolean;
        touchIds?: number[];
        x?: number;
        y?: number;
    } = {
            multiTouch: false,
            deltaXTotal: 0,
            deltaYTotal: 0,
            touchIds: [],
            x: 0,
            y: 0,
        };

    clearTouchDeltasTimer?: ReturnType<typeof setTimeout>;
    enableXScrollTimer?: ReturnType<typeof setTimeout>;
    enableYScrollTimer?: ReturnType<typeof setTimeout>;

    constructor(mainStore: MainStore) {
        makeObservable(this, {
            clearTouchDeltasTimer: observable,
            onMount: action.bound,
            onTickHistory: action.bound,
            onTouch: action.bound,
            onChartLoad: action.bound,
            onTick: action.bound,
            loadHistory: action.bound,
            onVisibleAreaChanged: action.bound,
            onQuoteAreaChanged: action.bound,
            setMsPerPx: action.bound,
            newChart: action.bound,
            enableXScrollTimer: observable,
            enableYScrollTimer: observable,
            scale: action.bound,
            scrollableChartParent: computed,
            toggleDataFitMode: action.bound,
            toggleXScrollBlock: action.bound,
            touchValues: observable,
            onCrosshairMove: action.bound,
            isDataFitModeEnabled: observable,
            isChartLoaded: observable,
            replayFeedCount: observable,
            epochBounds: observable.ref,
            quoteBounds: observable.ref,
            msPerPx: observable,
            isFeedLoaded: observable,
            cleanChart: action.bound,
        });

        this.mainStore = mainStore;
        this.cleanChart();
        this.initFlutterCharts();
    }

    checkIndicatorHover = (
        dx: number,
        dy: number,
        dxLocal: number,
        dyLocal: number,
        bottomIndicatorIndex: number | undefined
    ) => {
        // dxLocal and dyLocal are the local position value correponding to the bottom indicator/main chart
        const epoch = this.flutterChart?.crosshair.getEpochFromX(dxLocal) || 0;
        const quote = (this.flutterChart?.crosshair.getQuoteFromY(dyLocal) || 0).toFixed(
            this.mainStore.crosshair.decimalPlaces
        );

        this.mainStore.crosshair.onMouseMove(dx, dy, epoch, quote);
        const getClosestEpoch = this.mainStore.chart.feed?.getClosestValidEpoch;
        const granularity = this.mainStore.chartAdapter.getGranularityInMs();

        const hoverIndex = this.flutterChart?.app.getIndicatorHoverIndex(
            dxLocal,
            dyLocal,
            getClosestEpoch,
            granularity,
            bottomIndicatorIndex
        );
        if (this.isScaled) {
            this.isScaled = false;
            return;
        }

        this.mainStore.studies.highlightIndicator(hoverIndex, dx, dy);
    };

    debouncedIndicatorHover = debounce(this.checkIndicatorHover, 5);

    initFlutterCharts() {
        window.jsInterop = {
            onChartLoad: this.onChartLoad,
            onMainSeriesPaint: this.painter.onPaint,
            onVisibleAreaChanged: this.onVisibleAreaChanged,
            onQuoteAreaChanged: this.onQuoteAreaChanged,
            loadHistory: this.loadHistory,
            onCrosshairDisappeared: () => {
                this.mainStore.crosshair.updateVisibility(false);
            },
            onCrosshairHover: (dx, dy, dxLocal, dyLocal, bottomIndicatorIndex) => {
                this.onCrosshairMove(dx, dy, dxLocal, dyLocal, bottomIndicatorIndex);

                if (this.drawingHoverIndex != null) {
                    const drawingRepoItems = this.mainStore.chartAdapter.flutterChart?.drawingTool
                        .getDrawingToolsRepoItems()
                        .map(item => safeParse(item))
                        .filter(item => item);

                    if (!drawingRepoItems) {
                        return;
                    }

                    const item = drawingRepoItems[this.drawingHoverIndex];

                    if (item) {
                        this.mainStore.crosshair.renderDrawingToolToolTip(
                            capitalize(item.name.replace('dt_', '')) || '',
                            dx,
                            dy
                        );
                    }
                }

                this.debouncedIndicatorHover(dx, dy, dxLocal, dyLocal, bottomIndicatorIndex);
            },
            indicators: {
                onRemove: (index: number) => {
                    this.mainStore.studies.deleteStudy(index);
                },
                onEdit: (index: number) => {
                    this.mainStore.studies.editStudyByIndex(index);
                },
                onSwap: (index1: number, index2: number) => {
                    const { activeItems } = this.mainStore.studies;
                    [activeItems[index1], activeItems[index2]] = [activeItems[index2], activeItems[index1]];
                },
            },
            drawingTool: {
                onAdd: () => {
                    this.mainStore.drawTools.onCreation();
                },
                onUpdate: () => {
                    this.mainStore.drawTools.onUpdate();
                },
                onLoad: (items: []) => {
                    this.mainStore.drawTools.onLoad(items);
                },
                onMouseEnter: (index: number) => {
                    this.drawingHoverIndex = index;
                },
                onMouseExit: () => {
                    this.drawingHoverIndex = null;
                    this.mainStore.crosshair.removeDrawingToolToolTip();
                },
            },
        };

        // Don't create Flutter chart element in constructor - only when actually mounted
        // This allows new lightweight engine demos to avoid loading Flutter entirely
    }

    /**
     * Force-indicator recomputation after a replay rebuild.
     * Clears existing indicator series in Flutter and re-applies active configs
     * from StudyLegendStore. This is used on rewind to ensure indicators align
     * with the rebuilt price series.
     */
    async recomputeIndicators() {
        await when(() => this.isChartLoaded);
        // Clear indicators from Flutter model
        this.flutterChart?.indicators.clearIndicators();
        // Re-apply all active indicators
        const { activeItems } = this.mainStore.studies;
        activeItems.forEach((item, index) => {
            this.mainStore.studies.addOrUpdateIndicator(item, index);
        });
    }

    /**
     * Ensure the current replay cursor epoch is visible.
     * If the cursor is outside the viewport (overflowing X), auto-pan to last tick.
     */
    ensureCursorVisible(epoch: number) {
        const chart = this.flutterChart;
        if (!chart || !this.isChartLoaded) return;

        const x = chart.app.getXFromEpoch?.(epoch);
        const el = (window as any).flutterChartElement as HTMLDivElement | undefined;
        if (typeof x === 'number' && el) {
            const width = el.offsetWidth;
            const margin = 40; // buffer near edges
            const overflowsRight = x > (width - margin);
            const overflowsLeft = x < margin;
            
            // Only scroll if cursor is actually outside the viewport
            if (overflowsRight) {
                // Scroll right to keep cursor visible
                const deltaPx = x - (width - margin * 2);
                this._smoothScroll(deltaPx, 150);
            } else if (overflowsLeft) {
                // Scroll left to keep cursor visible
                const deltaPx = x - margin;
                this._smoothScroll(deltaPx, 150);
            }
        }
    }

    private _smoothScroll(totalPxShift: number, durationMs: number) {
        const chart = this.flutterChart;
        if (!chart?.app?.scroll) return;
        const start = performance.now();
        const step = (now: number) => {
            const t = Math.min(1, (now - start) / durationMs);
            // ease-out cubic
            const ease = 1 - Math.pow(1 - t, 3);
            const px = totalPxShift * ease;
            // Compute incremental shift by tracking last applied value
            if (!this._lastSmoothPx) this._lastSmoothPx = 0;
            const inc = px - this._lastSmoothPx;
            this._lastSmoothPx = px;
            chart.app.scroll(inc);
            if (t < 1) requestAnimationFrame(step);
            else this._lastSmoothPx = 0;
        };
        requestAnimationFrame(step);
    }

    private _lastSmoothPx = 0;

    onMount(element: HTMLDivElement) {
        console.log('[ChartAdapter] onMount called, element:', element);
        // Lazy-load Flutter chart only when legacy chart is actually mounted
        if (!window.flutterChartElement) {
            console.log('[ChartAdapter] Creating Flutter chart element...');
            createChartElement({
                onChartLoad: this.onChartLoad,
            });
            console.log('[ChartAdapter] Flutter chart element created:', window.flutterChartElement);
        } else {
            console.log('[ChartAdapter] Flutter chart element already exists');
        }
        element.appendChild(window.flutterChartElement);
        console.log('[ChartAdapter] Flutter chart element appended to container');
        const initState = window._flutter.initState;
        initState.isMounted = true;

        if (initState.isEngineIntialized && !initState.isInitialRunCompleted) {
            console.log('[ChartAdapter] Running chart app...');
            runChartApp();
        }

        window.flutterChartElement?.addEventListener('wheel', this.onWheel, { capture: true });
        window.flutterChartElement?.addEventListener('touchstart', this.onTouch, { capture: true });
        window.flutterChartElement?.addEventListener('touchmove', this.onTouch, { capture: true });
        window.flutterChartElement?.addEventListener('touchend', this.onTouch, { capture: true });
        window.flutterChartElement?.addEventListener('dblclick', this.onDoubleClick, { capture: true });
    }

    onUnmount() {
        window._flutter.initState.isMounted = false;

        window.flutterChartElement?.removeEventListener('wheel', this.onWheel, { capture: true });
        window.flutterChartElement?.removeEventListener('touchstart', this.onTouch, { capture: true });
        window.flutterChartElement?.removeEventListener('touchmove', this.onTouch, { capture: true });
        window.flutterChartElement?.removeEventListener('touchend', this.onTouch, { capture: true });
        window.flutterChartElement?.removeEventListener('dblclick', this.onDoubleClick, { capture: true });
        clearTimeout(this.clearTouchDeltasTimer);
        clearTimeout(this.enableXScrollTimer);
        clearTimeout(this.enableYScrollTimer);
    }

    onChartLoad() {
        console.log('[ChartAdapter] onChartLoad called');
        this.flutterChart = window.flutterChart;
        console.log('[ChartAdapter] flutterChart:', this.flutterChart ? 'available' : 'NOT available');
        console.log('[ChartAdapter] flutterChart.feed:', this.flutterChart?.feed ? 'available' : 'NOT available');
        if (!this.flutterChart) {
            console.warn('[ChartAdapter] flutterChart not yet mirrored to parent — deferring isChartLoaded');
            return;
        }
        this.isChartLoaded = true;
        console.log('[ChartAdapter] isChartLoaded set to true');
        // Ensure indicators API has safe stubs to avoid runtime errors in lightweight / partial engine mode
        const ind: any = this.flutterChart?.indicators;
        if (ind) {
            if (typeof ind.removeIndicator !== 'function') {
                ind.removeIndicator = (_idx: number) => { /* no-op stub */ };
            }
            if (typeof ind.clearIndicators !== 'function') {
                ind.clearIndicators = () => { /* no-op stub */ };
            }
        }
    }

    cleanChart() {
        // Clean charts if it's already loaded
        if (window.flutterChartElement) {
            this.updateLeftMargin();
            this.setShowInterval(false);
            this.setSymbolClosed(false);
        }
    }

    onTouch(e: TouchEvent) {
        // Prevent vertical scroll on the chart for touch devices by forcing scroll on a scrollable parent of the chart:
        const chartNode = this.mainStore.chart.chartNode;
        const flutterChart = document.querySelector('.flutter-chart') as HTMLElement;

        if (chartNode && this.scrollableChartParent && !this.mainStore.state.isVerticalScrollEnabled) {
            if (this.touchValues.multiTouch) {
                if (e.type === 'touchend') {
                    this.touchValues.touchIds = this.touchValues.touchIds?.filter(
                        id => id === e.changedTouches[0].identifier
                    );
                    this.touchValues = { multiTouch: !!this.touchValues.touchIds?.length };
                }
                return;
            }
            if (e.touches.length > 1) {
                this.touchValues = { multiTouch: true };
                this.touchValues.touchIds = Array.from(e.touches).map(touch => touch.identifier);
                return;
            }

            const { pageX, pageY } = e.changedTouches[0];

            if (['touchmove', 'touchend'].includes(e.type)) {
                const forcedScrollAreaWidth = chartNode.offsetWidth - this.mainStore.chart.yAxisWidth;
                const forcedScrollAreaHeight = chartNode.offsetHeight - this.mainStore.chart.xAxisHeight;

                const { top, left } = chartNode.getBoundingClientRect();
                const xCoord = pageX - left;
                const yCoord = pageY - top;
                const isForcedScrollArea = xCoord < forcedScrollAreaWidth && yCoord < forcedScrollAreaHeight;

                const isTouchOnYAxis = xCoord >= forcedScrollAreaWidth;

                if (this.touchValues.x && this.touchValues.y) {
                    const xDiff = this.touchValues.x - pageX;
                    const yDiff = this.touchValues.y - pageY;
                    const deltaXTotal = (this.touchValues.deltaXTotal ?? 0) + xDiff;
                    const deltaYTotal = (this.touchValues.deltaYTotal ?? 0) + yDiff;
                    const deltaX = e.type === 'touchend' ? Math.abs(deltaXTotal) : Math.abs(xDiff);
                    const deltaY = e.type === 'touchend' ? Math.abs(deltaYTotal) : Math.abs(yDiff);
                    const isVerticalScroll = deltaY > deltaX;
                    this.touchValues = { ...this.touchValues, deltaXTotal, deltaYTotal };

                    if (isForcedScrollArea && isVerticalScroll) {
                        if (!this.isXScrollBlocked) this.toggleXScrollBlock(true, flutterChart);
                    } else if (!this.isXScrollBlocked && isTouchOnYAxis) {
                        this.stopScroll(flutterChart);
                    }
                }
                this.touchValues = { ...this.touchValues, x: pageX, y: pageY };
                if (e.type === 'touchend' && this.isXScrollBlocked) {
                    this.enableXScrollTimer = setTimeout(() => {
                        this.toggleXScrollBlock(false, flutterChart);
                    }, 100);
                }
            }
            if (['touchstart', 'touchend'].includes(e.type)) {
                this.touchValues =
                    e.type === 'touchstart'
                        ? { x: pageX, y: pageY }
                        : { deltaYTotal: this.touchValues.deltaYTotal, deltaXTotal: this.touchValues.deltaXTotal };
            }
        }
    }

    onWheel = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Prevent vertical scroll on the chart on wheel devices by disabling pointer events to make chart parent scrollable:
        const chartNode = this.mainStore.chart.chartNode;
        if (chartNode && !this.mainStore.state.isVerticalScrollEnabled) {
            const nonScrollableAreaWidth = chartNode.offsetWidth - this.mainStore.chart.yAxisWidth;
            const { left } = chartNode.getBoundingClientRect();
            const isVerticalScroll = e.deltaY && e.deltaX === 0;
            const x = e.pageX - left;
            if (x < nonScrollableAreaWidth && isVerticalScroll) {
                if (this.enableYScrollTimer) return;
                chartNode.style.pointerEvents = 'none';
                this.enableYScrollTimer = setTimeout(() => {
                    chartNode.style.pointerEvents = 'auto';
                    this.enableYScrollTimer = undefined;
                }, 300);
                return;
            }
        }

        if (e.deltaX === 0 && e.deltaZ === 0) {
            const value = (100 - Math.min(10, Math.max(-10, e.deltaY))) / 100;
            this.scale(value);
        } else {
            window.flutterChart?.app.scroll(e.deltaX);
        }
        return false;
    };

    onDoubleClick = () => {
        if (this.drawingHoverIndex != null) {
            this.mainStore.drawTools.onSetting(this.drawingHoverIndex);
            this.mainStore.crosshair.removeDrawingToolToolTip();
        } else if (this.mainStore.studies.currentHoverIndex != null) {
            this.mainStore.studies.editStudyByIndex(this.mainStore.studies.currentHoverIndex);
            this.mainStore.studies.clearHoverItem(this.mainStore.studies.currentHoverIndex);
        }
    };

    onCrosshairMove(dx: number, dy: number, dxLocal: number, dyLocal: number, bottomIndicatorIndex?: number) {
        // dxLocal and dyLocal are the local position value correponding to the bottom indicator/main chart
        const epoch = this.flutterChart?.crosshair.getEpochFromX(dxLocal) || 0;
        const quote = (this.flutterChart?.crosshair.getQuoteFromY(dyLocal) || 0).toFixed(
            this.mainStore.crosshair.decimalPlaces
        );

        this.mainStore.crosshair.onMouseMove(dx, dy, epoch, quote);
        this.crossHairValue = {
            x: dx,
            y: dy,
            xLocal: dxLocal,
            yLocal: dyLocal,
            bottomIndex: bottomIndicatorIndex,
        };
    }

    /**
     * Mark that a programmatic scroll is about to happen.
     * This prevents the scroll from being detected as a user scroll.
     */
    markProgrammaticScroll() {
        this._isProgrammaticScroll = true;
        // Clear any existing timer
        if (this._programmaticScrollTimer) {
            clearTimeout(this._programmaticScrollTimer);
        }
        // Auto-reset after scroll animation completes (500ms should be enough)
        this._programmaticScrollTimer = setTimeout(() => {
            this._isProgrammaticScroll = false;
        }, 500);
    }

    onVisibleAreaChanged(leftEpoch: number, rightEpoch: number) {
        if (this.epochBounds.leftEpoch !== leftEpoch || this.epochBounds.rightEpoch !== rightEpoch) {
            this.epochBounds = {
                leftEpoch,
                rightEpoch,
            };
            
            // Skip user-scroll detection during programmatic scrolls
            if (this._isProgrammaticScroll) {
                return;
            }
            
            // Detect if user has scrolled away from the replay cursor position
            // If in replay mode and the replay cursor is no longer visible, disable auto-follow
            const replayController = this.mainStore.replayController;
            if (replayController && replayController.mode === 'replay' && replayController.currentEpoch) {
                const replayEpoch = replayController.currentEpoch;
                const buffer = 60000; // 1 minute buffer to avoid flickering
                
                // Only disable following if:
                // 1. We were following
                // 2. The replay cursor is now to the RIGHT of the visible area (user scrolled left into history)
                // User scrolls LEFT to see history → replay cursor goes off RIGHT side
                const userScrolledAwayFromReplay = replayEpoch > rightEpoch + buffer;
                
                if (replayController.shouldFollowReplay && userScrolledAwayFromReplay) {
                    console.log('[ChartAdapter] User scrolled away from replay cursor, disabling auto-follow');
                    console.log('[ChartAdapter] replayEpoch:', new Date(replayEpoch).toLocaleTimeString(), 'rightEpoch:', new Date(rightEpoch).toLocaleTimeString());
                    replayController.shouldFollowReplay = false;
                }
            }
        }

        if (this.crossHairValue) {
            const { x, y, yLocal, xLocal, bottomIndex } = this.crossHairValue;
            if (x !== 0 && y !== 0) {
                this.onCrosshairMove(x, y, xLocal, yLocal, bottomIndex);
            }
        }
    }

    onQuoteAreaChanged(topQuote: number, bottomQuote: number) {
        this.quoteBounds = {
            topQuote,
            bottomQuote,
        };
    }

    getGranularityInMs() {
        const granularity: number = this.mainStore.state.granularity || 1;
        // In replay mode, enforce minimum 60s granularity for Flutter chart display.
        // The state.granularity may be 1 (1-second update interval) which controls
        // how fast candles appear, but Flutter still needs the actual candle size (≥60s).
        const replayMode = this.mainStore.replayController?.mode;
        if ((replayMode === 'replay' || replayMode === 'paused') && granularity > 0 && granularity < 60) {
            return 60 * 1000;
        }
        return granularity * 1000;
    }

    newChart = async () => {
        this.isFeedLoaded = false;
        this.isDataFitModeEnabled = this.mainStore.chart.startWithDataFitMode || false;

        await when(() => this.isChartLoaded);

        this.flutterChart?.app.newChart({
            symbol: this.mainStore.chart.currentActiveSymbol?.symbol,
            granularity: this.getGranularityInMs(),
            chartType: this.mainStore.state.chartType,
            isLive: this.mainStore.chart.isLive || false,
            startWithDataFitMode: this.isDataFitModeEnabled,
            theme: this.mainStore.chartSetting.theme,
            msPerPx: this.msPerPx,
            pipSize: this.mainStore.chart.pip,
            isMobile: this.mainStore.chart.isMobile || false,
            yAxisMargin: this.mainStore.state.yAxisMargin,
        });
    };

    async onTickHistory(quotes: TQuote[]) {
        console.log('[ChartAdapter] onTickHistory called with', quotes.length, 'quotes');
        this.isFeedLoaded = true;

        console.log('[ChartAdapter] onTickHistory waiting for isChartLoaded...');
        await when(() => this.isChartLoaded);
        console.log('[ChartAdapter] onTickHistory isChartLoaded=true, updating chart');

        this.mainStore.chart.feed?.updateQuotes(quotes, false);
        console.log('[ChartAdapter] onTickHistory calling flutterChart.feed.onTickHistory');
        this.flutterChart?.feed.onTickHistory(quotes, false);
        console.log('[ChartAdapter] onTickHistory completed');
    }

    async onTick(quote: TQuote) {
        await when(() => this.isChartLoaded);

        const lastQuote = this.mainStore.chart.feed?.quotes[this.mainStore.chart.feed?.quotes.length - 1];
        if (lastQuote && new Date(lastQuote.Date) > new Date(quote.Date)) return;

        this.mainStore.chart.feed?.addQuote(quote);

        runInAction(() => {
            this.mainStore.chart.lastQuote = quote;
        });

        const granularity = this.getGranularityInMs();
        console.log('[ChartAdapter] onTick called - granularity:', granularity, 'quote.ohlc:', quote.ohlc, 'quote:', quote);
        
        // In candle mode with granularity >= 60 seconds, only send OHLC candle updates, not individual tick updates
        // The API sends both: OHLC candles (once per period) and tick updates (every second)
        // For shorter intervals (< 60s), the API may not send OHLC, so we need to accept tick updates
        if (granularity >= 60000 && !quote.ohlc) {
            console.log('[ChartAdapter] Skipping tick update in candle mode (waiting for OHLC candle)');
            return;
        }
        
        // Send data to Flutter chart
        if (quote.ohlc) {
            console.log('[ChartAdapter] Calling onNewCandle');
            this.flutterChart?.feed.onNewCandle(quote);
        } else {
            // Tick mode or short intervals: send tick updates
            console.log('[ChartAdapter] Calling onNewTick');
            this.flutterChart?.feed.onNewTick(quote);
        }
    }

    // Replay integration proxies
    async applyReplayBatch(quotes: TQuote[], append: boolean) {
        await when(() => this.isChartLoaded);
        console.log('[ChartAdapter] ═══════════════════════════════════════════════════════════');
        console.log('[ChartAdapter] 🎬 APPLYING REPLAY BATCH TO CHART');
        console.log('[ChartAdapter] Quotes count:', quotes.length);
        console.log('[ChartAdapter] Append mode:', append);
        
        if (quotes.length > 0) {
            const firstQuote = quotes[0];
            const lastQuote = quotes[quotes.length - 1];
            const firstTime = firstQuote?.Date || firstQuote?.DT;
            const lastTime = lastQuote?.Date || lastQuote?.DT;
            
            // DEBUG: Full OHLC inspection
            console.log('[ChartAdapter] 🔍 FIRST QUOTE OHLC:', {
                Open: firstQuote.Open,
                High: firstQuote.High,
                Low: firstQuote.Low,
                Close: firstQuote.Close,
                hasOHLC: firstQuote.Open !== undefined && firstQuote.High !== undefined && firstQuote.Low !== undefined,
            });
            
            if (firstTime) {
                console.log('[ChartAdapter] 🕒 ACTUAL REPLAY START TIME:', 
                    firstTime instanceof Date ? firstTime.toLocaleString() : new Date(firstTime).toLocaleString());
                console.log('[ChartAdapter] First quote epoch:', 
                    firstTime instanceof Date ? firstTime.getTime() : firstTime);
            }
            if (lastTime) {
                console.log('[ChartAdapter] Last quote time:', 
                    lastTime instanceof Date ? lastTime.toLocaleString() : new Date(lastTime).toLocaleString());
                console.log('[ChartAdapter] Last quote epoch:', 
                    lastTime instanceof Date ? lastTime.getTime() : lastTime);
            }
        }
        console.log('[ChartAdapter] ═══════════════════════════════════════════════════════════');
        
        if (this.flutterChart?.replay) {
            // IMPORTANT: Set replay cursor time BEFORE updating ticks to prevent race condition.
            // When ticks are updated, they trigger a widget rebuild. If the epoch is not yet updated,
            // the rebuild will use the old epoch to find the current tick, causing indicator mismatch.
            if (quotes.length > 0) {
                const lastQuote = quotes[quotes.length - 1];
                const lastTime = lastQuote?.Date || lastQuote?.DT;
                if (lastTime) {
                    const epoch = lastTime instanceof Date ? lastTime.getTime() : Date.parse(`${lastTime}Z`);
                    console.log('[ChartAdapter] Setting replay cursor time FIRST to:', epoch, new Date(epoch).toLocaleString());
                    this.flutterChart.replay.setCursorTime(epoch);
                }
            }
            
            // When append is false (replacing all data), use feed.onTickHistory to properly set isFeedLoaded flag
            // This is critical after newChart() which clears the chart and sets isFeedLoaded = false
            if (!append && this.flutterChart.feed) {
                console.log('[ChartAdapter] Using feed.onTickHistory for replace operation to set isFeedLoaded flag');
                this.flutterChart.feed.onTickHistory(quotes, false);
            } else {
                this.flutterChart.replay.applyTickBatch(quotes, append);
            }

            // Track replay feed calls so the loader overlay knows when data has been rendered
            runInAction(() => {
                this.replayFeedCount++;
            });
            console.log('[ChartAdapter] replayFeedCount incremented to:', this.replayFeedCount);
        } else {
            console.log('[ChartAdapter] Using fallback method');
            if (append) {
                for (const q of quotes) {
                    await this.onTick(q);
                }
            } else {
                await this.onTickHistory(quotes);
            }
        }
    }

    async resetToEpoch(targetEpoch: number) {
        await when(() => this.isChartLoaded);
        if (this.flutterChart?.replay) {
            this.flutterChart.replay.resetSeries();
            this.flutterChart.replay.setCursorTime(targetEpoch);
        } else {
            // No-op for lightweight; cursor concept not implemented
        }
    }

    loadHistory(payload: TLoadHistoryParams) {
        const { count, end } = payload;
        const { state, chart } = this.mainStore;
        // Use effectiveApiGranularity to avoid sending invalid values (e.g. 1) to the API
        const granularity = state.effectiveApiGranularity;
        const replayMode = this.mainStore.replayController.mode;
        const isReplayMode = replayMode === 'replay' || replayMode === 'paused';

        // In replay mode, allow loading historical data prior to the replay start date
        if (isReplayMode) {
            console.log('[ChartAdapter] Loading historical data during replay mode', {
                end: new Date(end * 1000).toLocaleString(),
                count,
                granularity,
            });

            // Fetch historical data using the Feed's pagination mechanism
            chart.feed?.fetchPaginationData(
                chart.currentActiveSymbol?.symbol as string,
                end,
                count,
                granularity,
                ({ quotes }) => {
                    if (quotes && quotes.length > 0) {
                        console.log('[ChartAdapter] Replay pagination received', quotes.length, 'quotes');
                        // Update the Feed's quotes array (prepend historical data)
                        this.mainStore.chart.feed?.updateQuotes(quotes, true);
                        // Update the Flutter chart with historical data (prepend)
                        this.flutterChart?.feed.onTickHistory(quotes, true);
                        
                        // After historical data is loaded, scroll to the replay position
                        // This ensures the chart shows the candle data, not an empty future time range
                        console.log('[ChartAdapter] Scrolling to last tick after historical data load');
                        setTimeout(() => {
                            this.flutterChart?.app.scrollToLastTick();
                        }, 100);
                    }
                }
            );
            return;
        }

        // Live mode: standard pagination behavior
        chart.feed?.fetchPaginationData(
            chart.currentActiveSymbol?.symbol as string,
            end,
            count,
            granularity,
            ({ quotes }) => {
                this.mainStore.chart.feed?.updateQuotes(quotes || [], true);
                this.flutterChart?.feed.onTickHistory(quotes || [], true);
            }
        );
    }

    async updateChartStyle(chartType: string) {
        await when(() => this.isChartLoaded);
        console.log('[ChartAdapter] updateChartStyle called with:', chartType);
        this.flutterChart?.config.updateChartStyle(chartType);
    }

    async updateGranularity(granularity: number) {
        await when(() => this.isChartLoaded);
        console.log('[ChartAdapter] updateGranularity called with:', granularity);
        this.flutterChart?.config.updateGranularity(granularity);
    }

    async updateTheme(theme: string) {
        await when(() => this.isChartLoaded);
        this.flutterChart?.config.updateTheme(theme);
    }

    async updateLeftMargin(leftMargin?: number) {
        await when(() => this.isChartLoaded);
        this.flutterChart?.config.updateLeftMargin(leftMargin);
    }

    async updateLiveStatus(isLive: boolean) {
        await when(() => this.isChartLoaded);
        this.flutterChart?.config.updateLiveStatus(isLive);
    }

    async setSymbolClosed(isClosed: boolean) {
        await when(() => this.isChartLoaded);
        this.flutterChart?.config.setSymbolClosed(isClosed);
    }

    async updateRightPadding(rightPadding?: number) {
        await when(() => this.isChartLoaded);
        this.flutterChart?.config.updateRightPadding(rightPadding);
    }

    async setShowInterval(showInterval: boolean) {
        await when(() => this.isChartLoaded);
        this.flutterChart?.config.toggleTimeIntervalVisibility(showInterval);
    }

    scale(scale: number) {
        if (this.mainStore.studies.currentHoverIndex !== null) {
            this.isScaled = true;
        }

        this.isDataFitModeEnabled = false;
        const msPerPx = this.flutterChart?.app.scale(scale);

        if (msPerPx != null) {
            this.setMsPerPx(msPerPx);
        }
    }

    toggleXScrollBlock = (isBlocked = true, flutterChart: HTMLElement) => {
        this.isXScrollBlocked = isBlocked;
        if (flutterChart) {
            if (isBlocked) {
                flutterChart.style.overflowY = 'scroll';
                flutterChart.style.overflowX = 'hidden';
                flutterChart.style.overscrollBehavior = 'contain';
                flutterChart.style.touchAction = 'pan-y';
            } else {
                this.allowScroll(flutterChart);
            }
        }
    };

    allowScroll = (element: HTMLElement) => {
        let lastScrollTop = element.scrollTop;
        let isScrolling = false;
        let scrollTimeout: ReturnType<typeof setTimeout>;

        const monitorScroll = () => {
            if (element.scrollTop !== lastScrollTop) {
                isScrolling = true;
                lastScrollTop = element.scrollTop;

                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(() => {
                    this.stopScroll(element);
                }, 200);
            } else {
                isScrolling = false;
            }

            if (isScrolling) {
                requestAnimationFrame(monitorScroll);
            }
        };
        requestAnimationFrame(monitorScroll);
    };

    stopScroll = (element: HTMLElement) => {
        element.style.removeProperty('overflow-y');
        element.style.removeProperty('overflow-x');
        element.style.removeProperty('touch-action');
    };

    toggleDataFitMode = () => {
        this.isDataFitModeEnabled = !this.isDataFitModeEnabled;
        window.flutterChart?.app.toggleDataFitMode(this.isDataFitModeEnabled);
    };

    async updateContracts(contractsMarker: any[]) {
        const transformedContractsMarker = contractsMarker
            .filter(c => c.markers?.length > 0)
            .map(c => {
                c.markers.forEach((m: any) => {
                    if (!m.quote) {
                        const { price } = this.getInterpolatedPositionAndPrice(m.epoch * 1000) || {};
                        m.quote = price;
                    }
                });
                return c;
            });

        await when(() => this.isFeedLoaded);

        this.flutterChart?.config.updateContracts(transformedContractsMarker);
    }

    getInterpolatedPositionAndPrice = (epoch: number) => {
        if (!epoch) return;

        const date = moment.utc(epoch).toDate();

        const tickIdx = this.mainStore.chart.feed?.getClosestQuoteIndexForEpoch(epoch);

        // To not place markers in the middle of ticks.
        let x: number = this.getXFromEpoch(epoch);

        if (typeof tickIdx === 'number' && tickIdx > -1) {
            const bar = this.mainStore.chart.feed?.quotes[tickIdx];

            let price = bar ? bar.Close : null;
            let delta_x, delta_y, ratio;

            // Here we interpolate the pixel distance between two adjacent ticks.
            if (bar && (bar.DT as Date) < date) {
                const barNext = this.mainStore.chart.feed?.quotes[tickIdx + 1];
                const barPrev = tickIdx > 0 ? this.mainStore.chart.feed?.quotes[tickIdx - 1] : null;

                if (barNext && barNext.Close && (barNext.DT as Date) > date) {
                    delta_x = this.getXFromEpoch((barNext.DT as Date).getTime()) - x;

                    ratio =
                        (((date as unknown) as number) - (bar.DT as Date).getTime()) /
                        ((barNext.DT as Date).getTime() - (bar.DT as Date).getTime());

                    if (price) delta_y = barNext.Close - price;
                } else if (barPrev && barPrev.Close) {
                    delta_x = x - this.getXFromEpoch((barPrev.DT as Date).getTime());

                    ratio =
                        (((date as unknown) as number) - (bar.DT as Date).getTime()) /
                        ((bar.DT as Date).getTime() - (barPrev.DT as Date).getTime());

                    if (price) delta_y = price - barPrev.Close;
                }
            }

            if (ratio) {
                if (delta_x) {
                    x += ratio * delta_x;
                }

                if (price && delta_y) {
                    price += ratio * delta_y;
                }
            }

            return {
                x,
                price,
            };
        }
    };

    setMsPerPx(msPerPx?: number) {
        this.msPerPx = msPerPx;
        this.mainStore.state.saveLayout();
    }

    getXFromEpoch(epoch: number) {
        return this.flutterChart?.app.getXFromEpoch(epoch) ?? 0;
    }

    getYFromQuote(quote: number) {
        return this.flutterChart?.app.getYFromQuote(quote) ?? 0;
    }

    getEpochFromX(x: number) {
        return this.flutterChart?.app.getEpochFromX(x) ?? 0;
    }

    getQuoteFromY(y: number) {
        return this.flutterChart?.app.getQuoteFromY(y) ?? 0;
    }

    get scrollableChartParent() {
        const chartNode = this.mainStore.chart.chartNode;
        if (!chartNode) return undefined;
        let parent = chartNode.parentElement;
        while (parent) {
            const { overflow } = window.getComputedStyle(parent);
            if (overflow.split(' ').every(o => o === 'auto' || o === 'scroll')) {
                return parent;
            }
            parent = parent.parentElement;
        }
        return document.documentElement;
    }
}
